/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/

import { useEffect, useRef } from "react"
import { useSelector, useDispatch } from "react-redux"
import { AppState } from "store/store.type"
import { actionCreator } from "store/store"
import { useEnablePartialMenu } from "hooks/useEnablePartialMenu"
import { isMenuFilterActive } from "utils/menu_helper"
import { menuApi } from "queries/menuApi"
import { FoodmaxapipbMartItemMenuDisplayV2Response } from "generated/foodmaxapi.type"
import { BulkSelectionsMetadata } from "store/duck/menu.type"

/**
 * Expands bulkSelectionsV3 hierarchical state into a flat itemID → boolean map
 * by walking martMenuData (dept → subDept → items) and applying the
 * inclusion/exclusion rules.
 *
 * Only items that exist in martMenuData are included, which naturally
 * respects whatever server-side filter was used to fetch that data.
 */
function expandBulkSelectionsToItemSelections(
  bulkSelectionsV3: BulkSelectionsMetadata,
  martMenuData: FoodmaxapipbMartItemMenuDisplayV2Response,
): { [itemID: string]: boolean } {
  const result: { [itemID: string]: boolean } = {}
  const selectedDepts = bulkSelectionsV3.selectedDepartments ?? {}
  const selectedSubDepts = bulkSelectionsV3.selectedSubDepartments ?? {}
  const selectedItems = bulkSelectionsV3.selectedItems ?? {}
  const excludedSubDepts = bulkSelectionsV3.excludedSubDepartments ?? {}
  const excludedItems = bulkSelectionsV3.excludedItems ?? {}

  martMenuData.departments?.forEach(dept => {
    const deptID = dept.itemClassID
    const isDeptSelected = Boolean(selectedDepts[deptID])

    dept.subDepartments?.forEach(subDept => {
      const subDeptID = subDept.itemClassID
      const isSubDeptExcluded = Boolean(excludedSubDepts[subDeptID])
      const isSubDeptSelected = Boolean(selectedSubDepts[subDeptID])

      // A subdept is effectively selected when:
      // - its parent dept is selected AND it is not individually excluded, OR
      // - it is explicitly selected (independent of parent dept)
      const isEffectivelySelected =
        (isDeptSelected && !isSubDeptExcluded) || isSubDeptSelected

      if (isEffectivelySelected) {
        subDept.items?.forEach(item => {
          const itemID = item.itemID ?? ""
          if (itemID && !excludedItems[itemID]) {
            result[itemID] = true
          }
        })
      }
    })
  })

  // Individually selected items with no selected parent are always included
  Object.keys(selectedItems).forEach(itemID => {
    result[itemID] = true
  })

  return result
}

/**
 * Computes the delta between what bulkSelectionsV3 said was selected and what
 * itemSelections actually contains after the user may have made changes while
 * the filter was active. Only items that exist in martMenuData (the filter scope)
 * are considered — items outside the scope are untouched in bulkSelectionsV3.
 */
function computeFilterSyncDelta(
  bulkSelectionsV3: BulkSelectionsMetadata,
  itemSelections: { [itemID: string]: boolean } | undefined,
  martMenuData: FoodmaxapipbMartItemMenuDisplayV2Response,
): {
  newlyExcluded: { [itemID: string]: { subDeptID: string; deptID: string } }
  newlySelected: { [itemID: string]: { subDeptID: string; deptID: string } }
} {
  const newlyExcluded: {
    [itemID: string]: { subDeptID: string; deptID: string }
  } = {}
  const newlySelected: {
    [itemID: string]: { subDeptID: string; deptID: string }
  } = {}

  const selectedDepts = bulkSelectionsV3.selectedDepartments ?? {}
  const selectedSubDepts = bulkSelectionsV3.selectedSubDepartments ?? {}
  const excludedSubDepts = bulkSelectionsV3.excludedSubDepartments ?? {}
  const excludedItems = bulkSelectionsV3.excludedItems ?? {}
  const selectedItems = bulkSelectionsV3.selectedItems ?? {}

  martMenuData.departments?.forEach(dept => {
    const deptID = dept.itemClassID
    const isDeptSelected = Boolean(selectedDepts[deptID])

    dept.subDepartments?.forEach(subDept => {
      const subDeptID = subDept.itemClassID
      const isSubDeptExcluded = Boolean(excludedSubDepts[subDeptID])
      const isSubDeptSelected = Boolean(selectedSubDepts[subDeptID])
      const isParentSelected =
        (isDeptSelected && !isSubDeptExcluded) || isSubDeptSelected

      subDept.items?.forEach(item => {
        const itemID = item.itemID ?? ""
        if (!itemID) return

        // Was this item effectively selected according to bulkSelectionsV3?
        const wasSelected =
          !excludedItems[itemID] &&
          (isParentSelected || Boolean(selectedItems[itemID]))

        const isNowSelected = Boolean(itemSelections?.[itemID])

        if (wasSelected && !isNowSelected) {
          // User unchecked it while filter was active
          newlyExcluded[itemID] = { subDeptID, deptID }
        } else if (!wasSelected && isNowSelected) {
          // User checked it while filter was active
          newlySelected[itemID] = { subDeptID, deptID }
        }
        // wasSelected === isNowSelected → no change needed
      })
    })
  })

  return { newlyExcluded, newlySelected }
}

/**
 * Syncs multi-select state between bulkSelectionsV3 (used by V3 components
 * when no filter is active) and itemSelections (used by legacy components
 * when a filter is active) whenever the user toggles filters while the
 * multi-select drawer is open.
 *
 * bulkSelectionsV3 is the authoritative source of truth for the full selection.
 * itemSelections is a temporary flat view derived from it for the filtered UI.
 *
 * Filter OFF → ON (V3 → legacy):
 *   Derives itemSelections from the items visible in martMenuData that match
 *   the bulkSelectionsV3 hierarchy. bulkSelectionsV3 is NOT cleared, so the
 *   full selection (including out-of-filter-scope items) is preserved.
 *
 * Filter ON → OFF (legacy → V3):
 *   Computes the delta between the original bulkSelectionsV3 and the user's
 *   changes in itemSelections, then applies only those changes back to
 *   bulkSelectionsV3. Items outside the filter scope are left untouched.
 *
 * Note: martMenuData is read from the RTK Query cache — no extra network
 * request is made. The cache is populated when the filter is active.
 */
export const useSelectionSync = () => {
  const dispatch = useDispatch()
  const enablePartialMenu = useEnablePartialMenu()

  const {
    merchantID,
    merchantGroupID,
    overview: { sellingTime },
    itemFilter,
    drawer: { showItemSelection, itemSelections },
  } = useSelector((state: AppState) => state.menu)

  const bulkSelectionsV3 = useSelector(
    (state: AppState) => state.bulkSelectionsV3,
  )

  const isFilterActive = isMenuFilterActive(itemFilter, sellingTime)

  // Read from RTK Query cache without triggering a new network request.
  // The cache is populated by useMartItemMenuDisplayV2Query in useMenuOverviewItems
  // when enablePartialMenu=true and a filter is active.
  const martMenuData = useSelector(
    menuApi.endpoints.martItemMenuDisplayV2.select({
      merchantID: merchantID ?? "",
      merchantGroupID,
    }),
  )?.data

  // Refs keep the latest values accessible inside effects without adding
  // them to the dependency array (avoids retriggering on every render)
  const bulkSelectionsV3Ref = useRef(bulkSelectionsV3)
  bulkSelectionsV3Ref.current = bulkSelectionsV3

  const itemSelectionsRef = useRef(itemSelections)
  itemSelectionsRef.current = itemSelections

  const martMenuDataRef = useRef(martMenuData)
  martMenuDataRef.current = martMenuData

  // null = not yet initialized (skip sync on first render)
  const prevIsFilterActiveRef = useRef<boolean | null>(null)

  // Set when filter turns ON but martMenuData is not yet in cache
  const pendingV3ToLegacyRef = useRef(false)

  useEffect(() => {
    // Only sync when partial menu is enabled and the drawer is open.
    // When the drawer is closed, selections are already cleared on close.
    if (!enablePartialMenu || !showItemSelection) {
      prevIsFilterActiveRef.current = isFilterActive
      return
    }

    // Initialize on first render without triggering a sync
    if (prevIsFilterActiveRef.current === null) {
      prevIsFilterActiveRef.current = isFilterActive
      return
    }

    if (prevIsFilterActiveRef.current === isFilterActive) return

    const wasFilterActive = prevIsFilterActiveRef.current
    prevIsFilterActiveRef.current = isFilterActive

    if (!wasFilterActive && isFilterActive) {
      // Filter turned ON: derive itemSelections from visible items in bulkSelectionsV3.
      // bulkSelectionsV3 is intentionally NOT cleared — it remains the authoritative
      // selection so that out-of-filter-scope items are preserved for when the filter
      // is removed.
      const data = martMenuDataRef.current
      if (data) {
        const newSelections = expandBulkSelectionsToItemSelections(
          bulkSelectionsV3Ref.current,
          data,
        )
        dispatch(
          actionCreator.menu.toggleDrawer({ itemSelections: newSelections }),
        )
      } else {
        // martMenuData is still loading — perform sync once it arrives
        pendingV3ToLegacyRef.current = true
      }
    } else {
      // Filter turned OFF: compute the delta between what bulkSelectionsV3 expected
      // and what the user actually has in itemSelections, then apply only those
      // changes back. Items outside the filter scope are untouched.
      const data = martMenuDataRef.current
      if (data) {
        const delta = computeFilterSyncDelta(
          bulkSelectionsV3Ref.current,
          itemSelectionsRef.current,
          data,
        )
        dispatch(actionCreator.bulkSelectionsV3.applyFilterSyncDelta(delta))

        // Backfill totalItemsPerSubDepartment and totalSubDepartmentsPerDepartment
        // from martMenuData (full unfiltered menu) so that computeDeptCheckboxState
        // can resolve CHECKED vs INDETERMINATE without requiring the user to expand
        // each department. Without this, dept checkboxes show INDETERMINATE until
        // the user clicks to load subdepts even though all items are selected.
        data.departments?.forEach(dept => {
          const deptID = dept.itemClassID ?? ""
          if (!deptID) return
          dispatch(
            actionCreator.bulkSelectionsV3.setTotalSubDepartments({
              departmentID: deptID,
              totalSubDepartments: dept.subDepartments?.length ?? 0,
            }),
          )
          dept.subDepartments?.forEach(subDept => {
            const subDeptID = subDept.itemClassID ?? ""
            if (!subDeptID) return
            dispatch(
              actionCreator.bulkSelectionsV3.setTotalItems({
                subDepartmentID: subDeptID,
                totalItems: subDept.items?.length ?? 0,
              }),
            )
          })
        })
      }
      dispatch(actionCreator.menu.toggleDrawer({ itemSelections: {} }))
    }
  }, [isFilterActive, enablePartialMenu, showItemSelection, dispatch])

  // Handle the delayed OFF → ON sync when martMenuData arrives after filter toggle.
  // Guard: only apply if filter is still active and drawer is still open —
  // the user may have toggled the filter back or closed the drawer while loading.
  useEffect(() => {
    if (
      pendingV3ToLegacyRef.current &&
      martMenuData &&
      isFilterActive &&
      showItemSelection
    ) {
      pendingV3ToLegacyRef.current = false
      const newSelections = expandBulkSelectionsToItemSelections(
        bulkSelectionsV3Ref.current,
        martMenuData,
      )
      dispatch(
        actionCreator.menu.toggleDrawer({ itemSelections: newSelections }),
      )
    } else if (
      pendingV3ToLegacyRef.current &&
      (!isFilterActive || !showItemSelection)
    ) {
      // Filter toggled back or drawer closed before data arrived — cancel pending sync
      pendingV3ToLegacyRef.current = false
    }
  }, [martMenuData, isFilterActive, showItemSelection, dispatch])
}
